import moment from 'moment';
import { SagaReturnType, call, put, select, takeEvery } from 'redux-saga/effects';
import {
  offerOrdersCreateSucceeded,
  offerOrdersGetListSucceeded,
  doSucceededGetOfferOrderDetails,
  toastMessagesAdd,
  createOfferOrderCardPaymentIntentSucceeded,
  updateOfferOrderCardPaymentIntentSucceeded,
  doSucceededUpdateOfferOrder,
  submitCardPayment,
  updateOfferOrderCardPaymentIntent,
  doSucceededFailOfferOrder,
  doSucceededCancelOfferOrder,
  doSucceededCompleteOfferOrder,
} from 'src/actions';
import { getClientApi, OfferOrderApi } from 'src/data-communication';
import {
  CreateOfferOrderBodyDto,
  UpdateOfferOrderDto,
  CreateOfferOrderCardPaymentIntentDto,
  UpdateOfferOrderCardPaymentIntentDto,
  OfferOrderPaymentTypeDto,
  FailOfferOrderDto,
  CancelOfferOrderDto,
  CompleteOfferOrderDto,
} from 'src/dtos';
import { mapGetOfferOrdersResponseDtoToModel, mapOfferOrderDetailsDtoToModel } from 'src/mappers';
import { OfferOrder, OfferOrderIntent } from 'src/models';
import { v4 as uuidv4 } from 'uuid';

import { State, Type } from '../actions/utils';
import { SeverityEnum, TReduxAction } from '../typings/commonTypes';
import { assertPropertyIsFound } from '../utils/assertPropertyIsFound';

import { depositFunds } from './cashiering';
import { safeSaga } from './utils';

function* list(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);
  const payload = action.payload;
  const { data, total }: SagaReturnType<OfferOrderApi['list']> = yield call(getClientApi().offerOrder.list, {
    ...payload,
    authToken,
  });
  yield put(offerOrdersGetListSucceeded({ data: mapGetOfferOrdersResponseDtoToModel(data), total }));
}

function* retrieve(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);
  const payload = action.payload;
  const offerOrder: SagaReturnType<OfferOrderApi['retrieve']> = yield call(getClientApi().offerOrder.retrieve, {
    ...payload,
    authToken,
  });
  yield put(doSucceededGetOfferOrderDetails(mapOfferOrderDetailsDtoToModel(offerOrder)));
}

const shouldUpdateOfferOrderCardPaymentIntent = (intent: OfferOrderIntent) =>
  intent?.paymentType?.isCard && intent.cardPaymentIntentTotalCost !== intent.totalInvestment;

const createOfferOrderBody = (intent: OfferOrderIntent): CreateOfferOrderBodyDto => {
  const base = {
    offerId: intent.offerId,
    isAccreditedInvestor: intent.isAccreditedInvestor,
    localeDate: moment().format('M/D/YYYY'),
  };

  if (intent.isConditional) {
    return {
      ...base,
      paymentType: intent.paymentType?.value as Exclude<OfferOrderPaymentTypeDto, OfferOrderPaymentTypeDto.Card>,
      totalInvestment: intent.totalInvestment,
      retirementAccountFundingType: intent.retirementAccountFundingType,
    };
  } else {
    return {
      ...base,
      quantity: intent.quantity,
      paymentType: intent.paymentType?.value,
      cardPaymentIntentId: intent.paymentType?.isCard ? intent.cardPaymentIntentId : undefined,
      retirementAccountFundingType: intent.retirementAccountFundingType,
    };
  }
};

function* create(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);
  const offerOrderIntent: OfferOrderIntent & { accountId: number } = action.payload;

  const body: CreateOfferOrderBodyDto = createOfferOrderBody(offerOrderIntent);

  if (shouldUpdateOfferOrderCardPaymentIntent(offerOrderIntent)) {
    assertPropertyIsFound(offerOrderIntent, 'cardPaymentIntentId');
    assertPropertyIsFound(offerOrderIntent, 'cardPaymentAccountExternalId');
    yield call(
      updateCardPaymentIntent,
      updateOfferOrderCardPaymentIntent({
        params: {
          id: offerOrderIntent.cardPaymentIntentId,
        },
        body: {
          quantity: offerOrderIntent.quantity,
          cardPaymentAccount: { externalId: offerOrderIntent.cardPaymentAccountExternalId },
        },
      }),
    );
  }

  if (offerOrderIntent.paymentType?.isAch && offerOrderIntent.achDeposit) {
    yield call(depositFunds, {
      type: Type.CASHIERING_DEPOSIT_FUNDS,
      payload: {
        accountId: offerOrderIntent.accountId,
        body: {
          Amount: offerOrderIntent.achDeposit.amount,
          RelationshipId: offerOrderIntent.achDeposit.relationshipId,
        },
      },
    });
  }

  if (offerOrderIntent.isExternal && offerOrderIntent.securityDeliveryOption) {
    body.securityDeliveryOption = offerOrderIntent.securityDeliveryOption;
  }

  const response: SagaReturnType<OfferOrderApi['create']> = yield call(getClientApi().offerOrder.create, {
    params: { accountId: offerOrderIntent.accountId },
    body,
    authToken,
  });

  if (offerOrderIntent?.paymentType?.isCard) {
    yield put(submitCardPayment());
  }
  const model = mapOfferOrderDetailsDtoToModel(response);
  yield put(offerOrdersCreateSucceeded(model));

  const offerOrdersList: OfferOrder[] | undefined = yield select(state => state.offerOrders.list.data?.data);
  const total: number | undefined = yield select(state => state.offerOrders.list.data?.total);

  if (offerOrdersList && total !== undefined) {
    yield put(
      offerOrdersGetListSucceeded({
        data: [...offerOrdersList, model],
        total: total + 1,
      }),
    );
  }
}

function* update(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);

  const payload: UpdateOfferOrderDto = action.payload;
  const response: SagaReturnType<OfferOrderApi['update']> = yield call(getClientApi().offerOrder.update, {
    ...payload,
    authToken,
  });

  const model = mapOfferOrderDetailsDtoToModel(response);
  yield put(doSucceededUpdateOfferOrder(model));
}

function* cancel(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);

  const payload: CancelOfferOrderDto = action.payload;
  const response: SagaReturnType<OfferOrderApi['cancel']> = yield call(getClientApi().offerOrder.cancel, {
    ...payload,
    authToken,
  });

  yield put(
    toastMessagesAdd({
      key: uuidv4(),
      severity: SeverityEnum.Success,
      message: 'Offer order cancellation request was successfully submitted',
    }),
  );

  const model = mapOfferOrderDetailsDtoToModel(response);

  const offerOrdersList: OfferOrder[] | undefined = yield select(state => state.offerOrders.list.data?.data);
  const total: number | undefined = yield select(state => state.offerOrders.list.data?.total);

  if (offerOrdersList && total !== undefined) {
    yield put(
      offerOrdersGetListSucceeded({
        data: offerOrdersList.filter(anOfferOrder => anOfferOrder.id !== model.id).concat(model),
        total,
      }),
    );
  }

  yield put(doSucceededCancelOfferOrder(model));
}

function* fail(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);

  const payload: FailOfferOrderDto = action.payload;
  yield call(getClientApi().offerOrder.fail, {
    ...payload,
    authToken,
  });

  yield put(doSucceededFailOfferOrder());

  const offerOrdersList: OfferOrder[] | undefined = yield select(state => state.offerOrders.list.data?.data);
  const total: number | undefined = yield select(state => state.offerOrders.list.data?.total);

  if (offerOrdersList && total !== undefined) {
    yield put(
      offerOrdersGetListSucceeded({
        data: offerOrdersList.filter(anOfferOrder => anOfferOrder.id !== payload.params.id),
        total: total - 1,
      }),
    );
  }
}

function* complete(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);

  const payload: CompleteOfferOrderDto = action.payload;
  const response: SagaReturnType<OfferOrderApi['complete']> = yield call(getClientApi().offerOrder.complete, {
    ...payload,
    authToken,
  });

  const model = mapOfferOrderDetailsDtoToModel(response);

  yield put(doSucceededCompleteOfferOrder(model));
}

function* createCardPaymentIntent(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);

  const payload: CreateOfferOrderCardPaymentIntentDto = action.payload;
  const response: SagaReturnType<OfferOrderApi['createCardPaymentIntent']> = yield call(
    getClientApi().offerOrder.createCardPaymentIntent,
    {
      ...payload,
      authToken,
    },
  );

  yield put(createOfferOrderCardPaymentIntentSucceeded(response));
}

function* updateCardPaymentIntent(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);

  const payload: UpdateOfferOrderCardPaymentIntentDto = action.payload;
  const response: SagaReturnType<OfferOrderApi['updateCardPaymentIntent']> = yield call(
    getClientApi().offerOrder.updateCardPaymentIntent,
    {
      ...payload,
      authToken,
    },
  );

  yield put(updateOfferOrderCardPaymentIntentSucceeded(response));
}

/**
 * Offers sagas
 */
export function* registerOfferOrderSagas() {
  yield takeEvery(State.actionRequested(Type.GET_OFFER_ORDERS), safeSaga(list, Type.GET_OFFER_ORDERS));
  yield takeEvery(
    State.actionRequested(Type.GET_OFFER_ORDER_DETAILS),
    safeSaga(retrieve, Type.GET_OFFER_ORDER_DETAILS),
  );
  yield takeEvery(State.actionRequested(Type.CREATE_OFFER_ORDERS), safeSaga(create, Type.CREATE_OFFER_ORDERS, false));
  yield takeEvery(State.actionRequested(Type.UPDATE_OFFER_ORDER), safeSaga(update, Type.UPDATE_OFFER_ORDER));
  yield takeEvery(State.actionRequested(Type.CANCEL_OFFER_ORDER), safeSaga(cancel, Type.CANCEL_OFFER_ORDER));
  yield takeEvery(State.actionRequested(Type.FAIL_OFFER_ORDER), safeSaga(fail, Type.FAIL_OFFER_ORDER));
  yield takeEvery(State.actionRequested(Type.COMPLETE_OFFER_ORDER), safeSaga(complete, Type.COMPLETE_OFFER_ORDER));
  yield takeEvery(
    State.actionRequested(Type.CREATE_OFFER_ORDER_CARD_PAYMENT_INTENT),
    safeSaga(createCardPaymentIntent, Type.CREATE_OFFER_ORDER_CARD_PAYMENT_INTENT),
  );
  yield takeEvery(
    State.actionRequested(Type.UPDATE_OFFER_ORDER_CARD_PAYMENT_INTENT),
    safeSaga(updateCardPaymentIntent, Type.UPDATE_OFFER_ORDER_CARD_PAYMENT_INTENT),
  );
}
